// How the Code tab names and sorts sessions, using the server's session
// state and completed-turn projection:
//
// - A session is in Needs you only while a person must act: a question put
//   to them, or its open proposal waits for a passkey approval or is held by
//   a stopped batch. It is at work while its turn runs, one of its workers'
//   does, or its open proposal moves through checks, review and landing.
//   Otherwise it is done: its turn is over and nothing is asked, until a new
//   prompt. An ended session is never done: every ended session is recent,
//   whatever its issue or outcome (Poulpe-586). The groups read Needs you,
//   Done, Working, Recent.
// - Its title is its issue's; else the agent's own title for it; else the
//   first line of its first prompt. A worker, which works on its lead's
//   issue, is named by the last two alone. The server keeps those two as the
//   session's title. For a session it has no title of, they are read from
//   its transcript, which the page follows only for the session open and a
//   worker open in the pane: what was read of them is kept for the page's
//   life (titles.ts), and a row that has neither says so.
// - Its agent is the one of its runner that offers its model: the server
//   keeps the model, or the agent's own name when none was chosen.
// - A worker is a session another delegated: it is reached through its
//   lead, in the side pane, and not in the list while its lead is.
import { awaitingPerson, landingsAwaitingPerson, waitsOn } from '../sessions/attention'
import { putTo } from '../questions/asks'
import type { Issue, Landing, Proposal, Question, Runner, RunnerAgent, Session, WorkerVerdict, WorkerVerdictValue } from '../api/types'

export type SessionGroup = 'needs' | 'working' | 'done' | 'recent'

/** How a session is drawn: arms at work, a dot otherwise. Today's names. */
export type Shown = 'working' | 'waiting' | 'idle' | 'ended' | 'failed'

/** What the agent's transcript said, as far as it was read: its own title and the first prompt. */
export interface KnownTitle {
  agent: string | null
  prompt: string | null
}

const byId = (a: { id: string }, b: { id: string }) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
const lastActiveFirst = (a: Session, b: Session) => Date.parse(b.last_active_at) - Date.parse(a.last_active_at) || byId(b, a)
const newestFirst = (a: Session, b: Session) => b.created_at.localeCompare(a.created_at) || byId(b, a)

/** The open questions of each session that asked any, the oldest first. Ids are UUID v7: they sort by when they were asked. */
export function openQuestions(questions: ReadonlyMap<string, Question>, pendingAnswers?: ReadonlySet<string>): Map<string, Question[]> {
  const asked = new Map<string, Question[]>()
  for (const question of [...questions.values()].filter((q) => q.state === 'open' || pendingAnswers?.has(q.id)).sort(byId)) {
    const list = asked.get(question.session)
    if (list) list.push(question)
    else asked.set(question.session, [question])
  }
  return asked
}

/** Its turn is under way. */
export function isBusy(session: Pick<Session, 'state'>): boolean {
  return session.state === 'starting' || session.state === 'running'
}

/** How a session is drawn, `asking` while it has an open question. */
export function shownState(session: Pick<Session, 'state' | 'ended'>, asking = false): Shown {
  if (session.state === 'ended') return session.ended?.outcome === 'failed' ? 'failed' : 'ended'
  if (asking) return 'waiting'
  return session.state === 'idle' ? 'idle' : 'working'
}

/** What a session is doing, in words: a turn that has completed is done. */
export function stateText(session: Pick<Session, 'state' | 'ended' | 'resuming' | 'resumed_after_restart' | 'origin' | 'awaiting_person_since' | 'queued' | 'pending_place'>, asking = false): string {
  if (session.state === 'starting' && !asking) return session.resuming ? (session.resumed_after_restart ? 'Resuming after a restart' : 'Resuming') : 'Starting'
  switch (shownState(session, asking)) {
    case 'working':
      return 'Working'
    case 'waiting':
      return 'Waiting for an answer'
    case 'idle':
      return awaitingPerson(session) ? 'Done' : session.resumed_after_restart ? 'Resumed after a restart' : 'Idle'
    case 'failed':
      return 'Failed'
    default:
      return session.ended?.outcome === 'interrupted' ? 'Interrupted' : 'Ended'
  }
}

/** What places a session in the list, besides the session itself. */
export interface Standing {
  /** One of its open questions is put to the person. */
  asksMe: boolean
  /** Its open proposal, if it has one. */
  proposal?: Pick<Proposal, 'id'> | null
  /** A landing waits for a person on its open proposal: a passkey approval, or a stopped batch that holds it. */
  approval?: boolean
  /** One of its workers' turns is under way. */
  crew?: boolean
}

/** Where a session stands in the list. */
export function groupOf(session: Pick<Session, 'state'>, standing: Standing): SessionGroup {
  if (session.state === 'ended') return 'recent'
  if (standing.asksMe || standing.approval) return 'needs'
  if (isBusy(session) || standing.crew || standing.proposal) return 'working'
  return 'done'
}

/** What the board holds around the sessions: what `standingOf` reads. */
export interface Around {
  /** The open questions of each session that asked any. */
  asked: ReadonlyMap<string, readonly Question[]>
  member?: string | null
  /** The open proposals, by id. */
  proposals?: ReadonlyMap<string, Proposal>
  /** The running landings, and the batches waiting to run, by id. */
  landings?: ReadonlyMap<string, Landing>
  /** The stopped batches that hold an open proposal, and those dismissed on this page. */
  held?: ReadonlyMap<string, Landing>
  dismissed?: ReadonlySet<string>
  /** Every session, workers among them: a lead is at work while one of its workers is. */
  sessions?: readonly Session[]
}

/** What places a session, read from the board. */
export function standingOf(session: Pick<Session, 'id'>, around: Around): Standing {
  const proposal = [...(around.proposals?.values() ?? [])].find((p) => p.session_id === session.id) ?? null
  const waiting = landingsAwaitingPerson(around.landings ?? new Map(), around.held ?? new Map(), around.dismissed)
  return {
    asksMe: around.asked.get(session.id)?.some((question) => putTo(question, around.member)) ?? false,
    proposal,
    approval: !!proposal && waiting.some((landing) => waitsOn(landing, proposal)),
    crew: workersOf(around.sessions ?? [], session.id).some(isBusy),
  }
}

const GROUPS: readonly { group: SessionGroup; title: string }[] = [
  { group: 'needs', title: 'Needs you' },
  { group: 'done', title: 'Done' },
  { group: 'working', title: 'Working' },
  { group: 'recent', title: 'Recent' },
]

/** Each existing group, most recently active first, with ids breaking ties. */
export function groupSessions(sessions: readonly Session[], around: Around): { group: SessionGroup; title: string; sessions: Session[] }[] {
  const groups = new Map(sessions.map((session) => [session.id, groupOf(session, standingOf(session, around))]))
  return GROUPS.map(({ group, title }) => ({
    group,
    title,
    sessions: sessions.filter((session) => groups.get(session.id) === group).sort(lastActiveFirst),
  })).filter((entry) => entry.sessions.length > 0)
}

/** Seven days of activity, including the boundary, and every open session. */
export function recentSessions(sessions: readonly Session[], now: number): Session[] {
  const since = now - 7 * 24 * 60 * 60_000
  return sessions.filter((session) => session.state !== 'ended' || Date.parse(session.last_active_at) >= since)
}

// --- workers ---------------------------------------------------------------------------------

/** The display parent: a worker's lead or a review's proposer. */
export function leadOf(session: Pick<Session, 'origin' | 'id'>): string | null {
  const parent = session.origin.kind === 'started' ? null : session.origin.parent
  return parent && parent !== session.id ? parent : null
}

/** The workers a lead delegated, the newest first. */
export function workersOf(sessions: Iterable<Session>, lead: string | null | undefined): Session[] {
  if (!lead) return []
  return [...sessions].filter((session) => leadOf(session) === lead).sort(newestFirst)
}

/** How many of a lead's workers have not ended. */
export function workersAtWork(sessions: Iterable<Session>, lead: string): number {
  return workersOf(sessions, lead).filter((worker) => worker.state !== 'ended').length
}

/**
 * The sessions the list shows: a worker is reached through its lead, so one
 * is left out while the list holds its lead. One whose lead it does not hold
 * stays, so that none is out of reach.
 */
export function listedSessions(sessions: readonly Session[]): Session[] {
  const ids = new Set(sessions.map((session) => session.id))
  return sessions.filter((session) => {
    const lead = leadOf(session)
    return lead === null || !ids.has(lead)
  })
}

// --- titles ----------------------------------------------------------------------------------

/** A text's first line that says something, cut at 140 characters. */
export function firstLine(text: string): string {
  const line = text.split('\n').map((part) => part.trim()).find((part) => part !== '') ?? ''
  return line.length > 140 ? `${line.slice(0, 140)}…` : line
}

/**
 * A session's title: its issue's, else the agent's own, else its first prompt's first line; '' when none is known.
 * The server keeps the last two as `kept`; what its transcript said (`known`) names a session it has no title of.
 * A title a person chose (`byPerson`) comes before the issue's.
 */
export function sessionTitle(issue: Pick<Issue, 'title'> | undefined, kept: string | null | undefined, known: KnownTitle | undefined, byPerson = false): string {
  if (byPerson && kept?.trim()) return kept.trim()
  if (issue && issue.title.trim() !== '') return issue.title
  const title = kept?.trim()
  if (title) return title
  const agent = known?.agent?.trim()
  if (agent) return agent
  return known?.prompt ? firstLine(known.prompt) : ''
}

/** A session's title as a tooltip or a heading names it in full: with its issue's key. */
export function fullTitle(issue: Pick<Issue, 'key' | 'title'> | undefined, kept: string | null | undefined, known: KnownTitle | undefined): string {
  return issue ? `${issue.key} · ${issue.title}` : sessionTitle(undefined, kept, known)
}

/**
 * The title of this session: a worker works on its lead's issue, which
 * would name every worker alike, so a worker is named by its agent's title
 * or its first prompt alone.
 */
export function titleOf(session: Pick<Session, 'origin' | 'id' | 'title' | 'title_by_person'>, issue: Pick<Issue, 'title'> | undefined, known: KnownTitle | undefined): string {
  return sessionTitle(leadOf(session) === null ? issue : undefined, session.title, known, session.title_by_person)
}

// --- agents and runners ------------------------------------------------------------------

/** The agent of its runner that a session runs: the one its start named, else the one named by the model the server keeps, or the one that offers it. */
export function agentOf(session: Pick<Session, 'runner_id' | 'agent' | 'runner_agent'>, runners: readonly Runner[] | null | undefined): RunnerAgent | null {
  const agents = runners?.find((runner) => runner.id === session.runner_id)?.agents ?? []
  const { model, vendor } = session.agent
  if (session.runner_agent) return agents.find((agent) => agent.agent === session.runner_agent) ?? null
  return (
    agents.find((agent) => agent.agent === model) ??
    agents.find((agent) => agent.vendor === vendor && agent.models.includes(model)) ??
    agents.find((agent) => agent.models.includes(model)) ??
    null
  )
}

const AGENT_NAMES: Record<string, string> = { claude: 'Claude', 'claude-api': 'Claude API', codex: 'Codex' }

/** The runner's agent that runs Claude Code on an API key rather than the plan's sign-in (Poulpe-585). */
export const CLAUDE_API = 'claude-api'

/** The agent whose ways an agent has: Claude API is Claude, on another bill. */
export function engineOf(agent: string): string {
  return agent === CLAUDE_API ? 'claude' : agent
}

/** An agent by the name a person reads: Claude, Codex, or as its runner names it. */
export function agentName(agent: string): string {
  return Object.hasOwn(AGENT_NAMES, agent) ? AGENT_NAMES[agent]! : agent
}

/**
 * What a row and the strip say of a session's agent: its name, the model when
 * one was chosen, the class of its colour (its engine's), and whether it is
 * billed to an API key. The agent its start named holds even once its runner
 * offers it no more.
 */
export function agentLabel(session: Pick<Session, 'runner_id' | 'agent' | 'runner_agent'>, runners: readonly Runner[] | null | undefined): { id: string | null; name: string; model: string | null; api: boolean } {
  const agent = agentOf(session, runners)?.agent ?? session.runner_agent ?? null
  const { model, vendor } = session.agent
  if (!agent) return { id: null, name: vendor, model, api: false }
  return { id: engineOf(agent), name: agentName(agent), model: model === agent ? null : model, api: agent === CLAUDE_API }
}

/** The workspace's runners a session may be on: those not revoked. */
export function activeRunners(runners: readonly Runner[] | null | undefined): Runner[] {
  return (runners ?? []).filter((runner) => !runner.revoked)
}

/** Why the session's runner takes no steering from the pages; null when it does, or is not known yet. */
export function runnerClosed(runner: Runner | undefined): string | null {
  if (runner?.revoked) return 'This session’s runner is revoked: it takes no more prompts.'
  if (runner && !runner.accepts_work_from_elsewhere) return 'This session’s runner is driven only from its own machine: steer it there.'
  return null
}

const VERDICTS: Record<WorkerVerdictValue, string> = { taken: 'Taken', taken_after_fixes: 'Taken after fixes', discarded: 'Discarded' }

/** The lead's verdict on a worker's result as a label and its reason (empty when it gave none); null for no verdict, or one this page does not know. */
export function verdictText(verdict: WorkerVerdict | null | undefined): { value: WorkerVerdictValue; label: string; reason: string } | null {
  const label = verdict && Object.prototype.hasOwnProperty.call(VERDICTS, verdict.value) ? VERDICTS[verdict.value] : undefined
  return verdict && label ? { value: verdict.value, label, reason: verdict.reason?.trim() ?? '' } : null
}

/** `1 subagent`, `2 subagents`. */
export function countText(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}
